/**
 * T+ Trading — Short-term swing 3-7 sessions
 */
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, CircularProgress, Skeleton, alpha,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
  Snackbar, Alert, LinearProgress, Chip,
} from '@mui/material';
import {
  Refresh, TrendingUp, ExitToApp, ShowChart, Assessment, Speed,
  CheckCircle, AccessTime, Warning as WarningIcon, Logout, Visibility,
  CallMade, Bolt, FiberManualRecord, History,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { tplusAPI, tplusPaperAPI } from '../services/api';
import SignalsPanel from '../components/tplus/SignalsPanel';
import PaperTradingPanel from '../components/tplus/PaperTradingPanel';
import WatchlistPanel from '../components/tplus/WatchlistPanel';
import SignalHistoryPanel from '../components/tplus/SignalHistoryPanel';

// ── Helpers ──
const fmt = (n) => n ? n.toLocaleString('vi-VN', { maximumFractionDigits: 0 }) : '0';
const fmtPct = (n, d = 2) => n != null ? `${n.toFixed(d)}%` : '0%';
const thSx = { fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.02em', py: 0.8, borderBottom: '1px solid #e2e8f0', bgcolor: '#fafbfc' };
const tdSx = { py: 0.7, fontSize: '0.8rem', borderBottom: '1px solid #f1f5f9' };

// ── Stat Tile ──
const Tile = ({ label, value, sub, color, Icon }) => (
  <Box sx={{
    position: 'relative', overflow: 'hidden',
    borderRadius: 1.5, border: `1px solid ${alpha(color, 0.12)}`,
    bgcolor: '#fff', p: '10px 12px', transition: 'all 0.2s',
    '&:hover': { borderColor: alpha(color, 0.25), boxShadow: `0 2px 8px ${alpha(color, 0.06)}` },
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
      <Box sx={{ width: 34, height: 34, borderRadius: 1.5, flexShrink: 0, background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 2px 6px ${alpha(color, 0.2)}` }}>
        <Icon sx={{ fontSize: 18, color: '#fff' }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: '#94a3b8', lineHeight: 1 }}>{label}</Typography>
        <Typography sx={{ fontSize: '1.4rem', fontWeight: 900, color, lineHeight: 1.2, mt: 0.2, fontFamily: 'monospace' }}>{value}</Typography>
        {sub && <Typography sx={{ fontSize: '0.62rem', color: '#b0b8c4', mt: 0.2, lineHeight: 1.2 }}>{sub}</Typography>}
      </Box>
    </Box>
  </Box>
);

// ── Empty State ──
const EmptyState = ({ Icon, title, subtitle }) => (
  <Box sx={{ py: 5, px: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
    <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: '#f8fafc', border: '1.5px dashed #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {Icon && <Icon sx={{ fontSize: 24, color: '#cbd5e1' }} />}
    </Box>
    <Box sx={{ textAlign: 'center' }}>
      <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: '#94a3b8', lineHeight: 1.4 }}>{title}</Typography>
      {subtitle && <Typography sx={{ fontSize: '0.72rem', color: '#cbd5e1', mt: 0.3, maxWidth: 400 }}>{subtitle}</Typography>}
    </Box>
  </Box>
);

// ── Positions Table ──
const PositionsTable = ({ positions, t }) => {
  if (!positions || positions.length === 0) {
    return <EmptyState Icon={Assessment}
      title={t('tplus.positions.emptyTitle')}
      subtitle={t('tplus.positions.emptyDesc')} />;
  }
  return (
    <TableContainer>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={thSx}>{t('tplus.header.ticker')}</TableCell>
            <TableCell sx={thSx}>Setup</TableCell>
            <TableCell align="right" sx={thSx}>{t('tplus.header.entry')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('tplus.header.current')}</TableCell>
            <TableCell align="right" sx={thSx}>P&L</TableCell>
            <TableCell align="center" sx={thSx}>{t('tplus.header.days')}</TableCell>
            <TableCell align="center" sx={thSx}>{t('tplus.header.sell')}</TableCell>
            <TableCell align="center" sx={thSx}>Flags</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {positions.map((p, i) => {
            const pnlClr = p.unrealized_pnl_pct > 0 ? '#10b981' : p.unrealized_pnl_pct < 0 ? '#ef4444' : '#64748b';
            const dayClr = p.hold_days >= 7 ? '#ef4444' : p.hold_days >= 5 ? '#f59e0b' : '#64748b';
            const setupClr = p.setup_type === 'Spring' ? '#10b981' : p.setup_type === 'NarrowSpring' ? '#059669' : p.setup_type === 'MAConverge' ? '#0ea5e9' : p.setup_type === 'GapAndGo' ? '#8b5cf6' : p.setup_type === 'ConfirmBreak' ? '#f59e0b' : '#3b82f6';
            return (
              <TableRow key={i} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                <TableCell sx={tdSx}><Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: '#1e293b' }}>{p.ticker}</Typography></TableCell>
                <TableCell sx={tdSx}><Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: setupClr }}>{p.setup_type}</Typography></TableCell>
                <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace' }}>{fmt(p.entry_price)}</TableCell>
                <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace' }}>{fmt(p.current_price)}</TableCell>
                <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace', fontWeight: 700, color: pnlClr }}>{p.unrealized_pnl_pct ? fmtPct(p.unrealized_pnl_pct) : '–'}</TableCell>
                <TableCell align="center" sx={{ ...tdSx, fontFamily: 'monospace', fontWeight: 700, color: dayClr }}>T+{p.hold_days}</TableCell>
                <TableCell align="center" sx={tdSx}>
                  {p.can_sell ? <CheckCircle sx={{ fontSize: 16, color: '#10b981' }} /> : (
                    <Tooltip title={`Can sell in ${p.days_until_sellable}d`}><AccessTime sx={{ fontSize: 16, color: '#f59e0b' }} /></Tooltip>
                  )}
                </TableCell>
                <TableCell align="center" sx={tdSx}>
                  <Box sx={{ display: 'flex', gap: 0.3, justifyContent: 'center' }}>
                    {p.tp1_hit && <Tooltip title="TP1 Hit"><TrendingUp sx={{ fontSize: 14, color: '#10b981' }} /></Tooltip>}
                    {p.stop_breached && <Tooltip title="Stop Breached"><WarningIcon sx={{ fontSize: 14, color: '#ef4444' }} /></Tooltip>}
                    {p.time_stop_triggered && <Tooltip title="Time-Stop"><AccessTime sx={{ fontSize: 14, color: '#f59e0b' }} /></Tooltip>}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// ── Exits Table ──
const ExitsTable = ({ exitRecs, t, onExecuteExit, exitLoading }) => {
  if (!exitRecs || exitRecs.length === 0) {
    return <EmptyState Icon={CheckCircle}
      title={t('tplus.exits.emptyTitle')}
      subtitle={t('tplus.exits.emptyDesc')} />;
  }
  return (
    <Box>
      <Box sx={{ px: 2.5, py: 1, borderBottom: '1px solid #f1f5f9', bgcolor: alpha('#f59e0b', 0.04) }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#f59e0b' }}>
          {t('tplus.exits.needAttention', { count: exitRecs.length })}
        </Typography>
      </Box>
      <TableContainer>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={thSx}>{t('tplus.header.ticker')}</TableCell>
              <TableCell align="center" sx={thSx}>{t('tplus.header.priority')}</TableCell>
              <TableCell sx={thSx}>{t('tplus.header.reason')}</TableCell>
              <TableCell sx={thSx}>{t('tplus.header.action')}</TableCell>
              <TableCell align="right" sx={thSx}>{t('tplus.header.entry')}</TableCell>
              <TableCell align="right" sx={thSx}>{t('tplus.header.current')}</TableCell>
              <TableCell align="right" sx={thSx}>P&L</TableCell>
              <TableCell align="center" sx={thSx}>{t('tplus.header.days')}</TableCell>
              <TableCell align="center" sx={thSx}>{t('tplus.header.execute')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {exitRecs.map((r, i) => {
              const pnlClr = r.pnl_pct > 0 ? '#10b981' : '#ef4444';
              const priClr = r.priority >= 4 ? '#ef4444' : r.priority >= 2 ? '#f59e0b' : '#94a3b8';
              const reasonClr = r.exit_reason === 'TP2' ? '#10b981' : r.exit_reason === 'StopBreach' ? '#ef4444' : '#f59e0b';
              const dayClr = r.hold_days >= 7 ? '#ef4444' : r.hold_days >= 5 ? '#f59e0b' : '#64748b';
              return (
                <TableRow key={i} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                  <TableCell sx={tdSx}><Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: '#1e293b' }}>{r.ticker}</Typography></TableCell>
                  <TableCell align="center" sx={tdSx}><Typography sx={{ fontWeight: 900, fontSize: '0.88rem', color: priClr, fontFamily: 'monospace' }}>{r.priority}</Typography></TableCell>
                  <TableCell sx={tdSx}><Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: reasonClr }}>{r.exit_reason}</Typography></TableCell>
                  <TableCell sx={tdSx}><Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>{r.action}</Typography></TableCell>
                  <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace' }}>{fmt(r.entry_price)}</TableCell>
                  <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace' }}>{fmt(r.current_price)}</TableCell>
                  <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace', fontWeight: 700, color: pnlClr }}>{fmtPct(r.pnl_pct)}</TableCell>
                  <TableCell align="center" sx={{ ...tdSx, fontFamily: 'monospace', fontWeight: 700, color: dayClr }}>T+{r.hold_days}</TableCell>
                  <TableCell align="center" sx={tdSx}>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={exitLoading === r.ticker}
                      onClick={() => onExecuteExit?.(r)}
                      startIcon={exitLoading === r.ticker ? <CircularProgress size={14} color="inherit" /> : <Logout sx={{ fontSize: 15 }} />}
                      sx={{
                        px: 1.5, py: 0.3, fontSize: '0.72rem', fontWeight: 700,
                        textTransform: 'none', borderRadius: 1.5, minWidth: 0,
                        background: `linear-gradient(135deg, #f59e0b 0%, ${alpha('#f59e0b', 0.8)} 100%)`,
                        boxShadow: `0 2px 6px ${alpha('#f59e0b', 0.25)}`,
                        '&:hover': { boxShadow: `0 3px 10px ${alpha('#f59e0b', 0.35)}` },
                      }}
                    >
                    {t('tplus.exits.exitBtn')}
                  </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ── Performance Panel ──
const PerformancePanel = ({ performance, scanResult, t }) => {
  const hasLive = performance && performance.total_trades > 0;
  const wr = performance?.win_rate || 0;
  const exp = performance?.expectancy || 0;
  const wrClr = wr >= 60 ? '#10b981' : wr >= 50 ? '#f59e0b' : '#ef4444';
  const expClr = exp > 0 ? '#10b981' : '#ef4444';

  // Live vs Backtest delta
  const wrDelta = hasLive ? wr - 64.1 : null;
  const expDelta = hasLive ? exp - 0.93 : null;

  const StatCard = ({ value, label, color = '#334155', delta, small }) => (
    <Box sx={{ textAlign: 'center', flex: 1 }}>
      <Typography sx={{ fontSize: small ? '1.1rem' : '1.5rem', fontWeight: 900, color, fontFamily: 'monospace', lineHeight: 1 }}>{value}</Typography>
      {delta != null && (
        <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, fontFamily: 'monospace', color: delta >= 0 ? '#10b981' : '#ef4444', mt: 0.3 }}>
          {delta >= 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}% vs BT
        </Typography>
      )}
      <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#94a3b8', mt: 0.4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 2 }}>
      {/* ── Main metrics row ── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: hasLive ? '1.1fr 0.9fr' : '1fr', gap: 1.5, mb: 1.5 }}>
        {/* Live card */}
        <Box sx={{
          borderRadius: 1.5, overflow: 'hidden',
          border: `1px solid ${hasLive ? alpha(wrClr, 0.15) : '#e2e8f0'}`,
          bgcolor: '#fff',
        }}>
          {/* Header bar */}
          <Box sx={{
            px: 1.5, py: 0.6, display: 'flex', alignItems: 'center', gap: 0.8,
            borderBottom: `1px solid ${hasLive ? alpha(wrClr, 0.1) : '#f1f5f9'}`,
            bgcolor: hasLive ? alpha(wrClr, 0.03) : '#fafbfc',
          }}>
            <FiberManualRecord sx={{ fontSize: 8, color: hasLive ? wrClr : '#cbd5e1' }} />
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: hasLive ? '#475569' : '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {t('tplus.performance.live')}
            </Typography>
            {hasLive && (
              <Chip label={`${performance.total_trades} trades`} size="small" sx={{
                ml: 'auto', height: 18, fontSize: '0.6rem', fontWeight: 700, fontFamily: 'monospace',
                bgcolor: alpha(wrClr, 0.08), color: wrClr, '& .MuiChip-label': { px: 0.8 },
              }} />
            )}
          </Box>
          {/* Body */}
          <Box sx={{ p: 1.5 }}>
            {hasLive ? (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <StatCard value={fmtPct(wr)} label="Win Rate" color={wrClr} delta={wrDelta} />
                <Box sx={{ width: '1px', height: 40, bgcolor: '#f1f5f9', flexShrink: 0 }} />
                <StatCard value={`${exp > 0 ? '+' : ''}${fmtPct(exp)}`} label="Expectancy" color={expClr} delta={expDelta} />
                <Box sx={{ width: '1px', height: 40, bgcolor: '#f1f5f9', flexShrink: 0 }} />
                <StatCard value={`-${fmtPct(performance.max_drawdown_pct)}`} label="Max DD" color="#ef4444" />
              </Box>
            ) : (
              <Box sx={{ py: 1, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.78rem', color: '#b0b8c4' }}>
                  {t('tplus.performance.noTrades')}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Backtest card */}
        <Box sx={{ borderRadius: 1.5, overflow: 'hidden', border: '1px solid #e8eaed', bgcolor: '#fafbfc' }}>
          <Box sx={{ px: 1.5, py: 0.6, display: 'flex', alignItems: 'center', gap: 0.8, borderBottom: '1px solid #f1f5f9' }}>
            <Assessment sx={{ fontSize: 13, color: '#94a3b8' }} />
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Backtest
            </Typography>
            <Chip label="145 trades" size="small" sx={{
              ml: 'auto', height: 18, fontSize: '0.6rem', fontWeight: 700, fontFamily: 'monospace',
              bgcolor: '#f1f5f9', color: '#94a3b8', '& .MuiChip-label': { px: 0.8 },
            }} />
          </Box>
          <Box sx={{ p: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <StatCard value="64.1%" label="Win Rate" color="#667eea" small />
              <Box sx={{ width: '1px', height: 32, bgcolor: '#e8eaed', flexShrink: 0 }} />
              <StatCard value="+0.93%" label="Expectancy" color="#667eea" small />
              <Box sx={{ width: '1px', height: 32, bgcolor: '#e8eaed', flexShrink: 0 }} />
              <StatCard value="4.3d" label="Avg Hold" color="#94a3b8" small />
              <Box sx={{ width: '1px', height: 32, bgcolor: '#e8eaed', flexShrink: 0 }} />
              <StatCard value="1.50:1" label="R:R" color="#94a3b8" small />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Setup breakdown ── */}
      {hasLive && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5 }}>
          {[
            { name: 'Spring', stats: performance.spring_stats, color: '#10b981', Icon: CallMade },
            { name: 'GapAndGo', stats: performance.gapandgo_stats, color: '#8b5cf6', Icon: Bolt },
            { name: 'ConfirmBreak', stats: performance.confirmbreak_stats, color: '#f59e0b', Icon: TrendingUp },
            { name: 'MAConverge', stats: performance.maconverge_stats, color: '#0ea5e9', Icon: Speed },
          ].map(s => {
            const cnt = s.stats?.count || 0;
            const swr = s.stats?.win_rate || 0;
            const pnl = s.stats?.avg_pnl || 0;
            return (
              <Box key={s.name} sx={{ borderRadius: 1.5, overflow: 'hidden', border: `1px solid ${alpha(s.color, 0.12)}`, bgcolor: '#fff' }}>
                {/* Setup header */}
                <Box sx={{
                  px: 1.5, py: 0.6, display: 'flex', alignItems: 'center', gap: 0.6,
                  borderBottom: `1px solid ${alpha(s.color, 0.08)}`, bgcolor: alpha(s.color, 0.02),
                }}>
                  <s.Icon sx={{ fontSize: 14, color: s.color }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#334155' }}>{s.name}</Typography>
                  <Chip label={cnt} size="small" sx={{
                    ml: 'auto', height: 18, fontSize: '0.62rem', fontWeight: 800, fontFamily: 'monospace',
                    bgcolor: alpha(s.color, 0.08), color: s.color, '& .MuiChip-label': { px: 0.6 },
                  }} />
                </Box>
                {/* Setup metrics */}
                <Box sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' }}>Win Rate</Typography>
                    <Typography sx={{ fontSize: '0.88rem', fontWeight: 900, fontFamily: 'monospace', color: swr >= 50 ? '#10b981' : '#ef4444' }}>{fmtPct(swr)}</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate" value={Math.min(swr, 100)}
                    sx={{
                      height: 4, borderRadius: 2, mb: 1.2,
                      bgcolor: '#f1f5f9',
                      '& .MuiLinearProgress-bar': { borderRadius: 2, bgcolor: swr >= 50 ? '#10b981' : '#ef4444' },
                    }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' }}>Avg P&L</Typography>
                    <Typography sx={{ fontSize: '0.88rem', fontWeight: 900, fontFamily: 'monospace', color: pnl >= 0 ? '#10b981' : '#ef4444' }}>
                      {pnl >= 0 ? '+' : ''}{fmtPct(pnl)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
          </Box>
      )}
    </Box>
  );
};

// ══════════════════════════════════════
const TPlus = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const [positions, setPositions] = useState([]);
  const [exitRecs, setExitRecs] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [paperPositions, setPaperPositions] = useState({ active: [], closed: [], metrics: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [exitLoading, setExitLoading] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  const [watchlistLoading] = useState(false);

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  const fetchAll = async () => {
    setLoading(true); setError(null);
    try {
      await Promise.all([
        tplusAPI.scan().then(r => setScanResult(r.data.data)).catch(() => {}),
        tplusAPI.getPositions().then(r => setPositions(r.data.positions || [])).catch(() => {}),
        tplusAPI.getExits().then(r => setExitRecs(r.data.recommendations || [])).catch(() => {}),
        tplusAPI.getPerformance().then(r => setPerformance(r.data.performance)).catch(() => {}),
        tplusPaperAPI.getPositions().then(r => setPaperPositions({ active: r.data.active_positions || [], closed: r.data.closed_positions || [], metrics: r.data.metrics || null })).catch(() => {}),
        tplusAPI.getWatchlist().then(r => setWatchlist(r.data.watchlist || [])).catch(() => {}),
      ]);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleCreatePosition = async (data) => {
    try {
      await tplusAPI.createPosition(data);
      setSnackbar({ open: true, message: t('tplus.snackbar.positionCreated', { ticker: data.ticker }), severity: 'success' });
      fetchAll();
      return true;
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      setSnackbar({ open: true, message: `${t('tplus.error')}: ${msg}`, severity: 'error' });
      return false;
    }
  };

  const handleExecuteExit = async (rec) => {
    setExitLoading(rec.ticker);
    try {
      await tplusAPI.executeExit({
        ticker: rec.ticker,
        entry_date: rec.entry_date,
        exit_date: new Date().toISOString().slice(0, 10),
        exit_price: rec.current_price,
        exit_reason: rec.exit_reason,
        partial: false,
      });
      setSnackbar({ open: true, message: t('tplus.snackbar.exitSuccess', { ticker: rec.ticker }), severity: 'success' });
      fetchAll();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      setSnackbar({ open: true, message: `${t('tplus.error')}: ${msg}`, severity: 'error' });
    } finally {
      setExitLoading(null);
    }
  };

  const TABS = [
    { key: 'watchlist', label: t('tplus.tab.watchlist'), color: '#8b5cf6', Icon: Visibility, count: watchlist.length },
    { key: 'signals', label: t('tplus.tab.signals'), color: '#667eea', Icon: ShowChart, count: scanResult?.signals?.length || 0 },
    { key: 'positions', label: t('tplus.tab.positions'), color: '#3b82f6', Icon: Assessment, count: positions.length },
    { key: 'exits', label: t('tplus.tab.exits'), color: '#f59e0b', Icon: ExitToApp, count: exitRecs.length },
    { key: 'perf', label: t('tplus.tab.performance'), color: '#10b981', Icon: TrendingUp },
    { key: 'history', label: t('tplus.tab.history'), color: '#667eea', Icon: History },
    { key: 'paper', label: 'Auto Demo', color: '#8b5cf6', Icon: Speed, count: paperPositions.active.length },
  ];

  return (
    <Box sx={{ p: 2, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 1.5,
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(102, 126, 234, 0.3)',
          }}>
            <ShowChart sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', letterSpacing: -0.3 }}>T+ Trading</Typography>
              {scanResult?.scan_date && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#f1f5f9', px: 1, py: 0.3, borderRadius: 1.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#667eea', boxShadow: '0 0 6px #667eea' }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>{scanResult.scan_date}</Typography>
                </Box>
              )}
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>
              {t('tplus.subtitle')}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={14} /> : <Refresh />}
          onClick={fetchAll} disabled={loading}
          sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '4px',
            color: '#64748b', borderColor: '#e2e8f0',
            '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' },
            '& .MuiButton-startIcon': { mr: 0.5 } }}
        >
          {loading ? t('tplus.loading') : t('tplus.refresh')}
        </Button>
      </Box>

      {/* Stat tiles */}
      {loading && !scanResult ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.2, mb: 1.5 }}>
          {[0,1,2,3].map(i => (
            <Box key={i} sx={{ borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff', p: '10px 12px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                <Skeleton variant="rounded" width={34} height={34} sx={{ borderRadius: 1.5 }} />
                <Box sx={{ flex: 1 }}><Skeleton width={60} height={12} sx={{ mb: 0.5 }} /><Skeleton width={40} height={22} /></Box>
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.2, mb: 1.5 }}>
          <Tile label={t('tplus.tile.todaySignals')} value={scanResult?.signals?.length || 0} sub="Spring · GapAndGo · ConfirmBreak · MAConverge" color="#667eea" Icon={ShowChart} />
          <Tile label={t('tplus.tile.activePositions')} value={positions.length} sub="Real-time tracking" color="#3b82f6" Icon={Assessment} />
          <Tile label={t('tplus.tile.winRate')} value={fmtPct(performance?.win_rate || 64.1, 1)} sub="Target: 64.1%" color="#10b981" Icon={TrendingUp} />
          <Tile label="Expectancy" value={fmtPct(performance?.expectancy || 0.93)} sub="Per trade" color="#06b6d4" Icon={Speed} />
        </Box>
      )}

      {/* Error */}
      {error && (
        <Box sx={{ px: 2, py: 1, mb: 2, borderRadius: 1.5, bgcolor: alpha('#ef4444', 0.06), border: `1px solid ${alpha('#ef4444', 0.15)}` }}>
          <Typography sx={{ fontSize: '0.8rem', color: '#ef4444' }}>{error}</Typography>
        </Box>
      )}

      {/* Tab bar */}
      <Box sx={{ display: 'flex', gap: 0, borderBottom: '2px solid #e2e8f0', mb: 0 }}>
        {TABS.map((tab, idx) => {
          const active = activeTab === idx;
          return (
            <Box key={tab.key} onClick={() => setActiveTab(idx)} sx={{
              display: 'flex', alignItems: 'center', gap: 0.6, px: 1.8, py: 0.9, cursor: 'pointer',
              borderBottom: active ? `2.5px solid ${tab.color}` : '2.5px solid transparent', mb: '-2px',
              transition: 'all 0.15s', '&:hover': { bgcolor: alpha(tab.color, 0.04) },
            }}>
              <tab.Icon sx={{ fontSize: 16, color: active ? tab.color : '#94a3b8' }} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: active ? 800 : 600, color: active ? tab.color : '#64748b' }}>{tab.label}</Typography>
              {tab.count != null && (
                <Box sx={{ display: 'inline-flex', px: 0.8, py: 0.15, borderRadius: 1, bgcolor: active ? alpha(tab.color, 0.1) : '#f1f5f9', minWidth: 24, justifyContent: 'center' }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, fontFamily: 'monospace', color: active ? tab.color : '#94a3b8' }}>{tab.count}</Typography>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Tab content */}
      <Box sx={{ borderRadius: '0 0 6px 6px', border: '1px solid #e2e8f0', borderTop: 'none', bgcolor: '#fff', overflow: 'hidden' }}>
        {activeTab === 0 && <WatchlistPanel watchlist={watchlist} loading={watchlistLoading} />}
        {activeTab === 1 && <SignalsPanel scanResult={scanResult} formatNumber={fmt} formatPercent={fmtPct} onCreatePosition={handleCreatePosition} />}
        {activeTab === 2 && <PositionsTable positions={positions} t={t} />}
        {activeTab === 3 && <ExitsTable exitRecs={exitRecs} t={t} onExecuteExit={handleExecuteExit} exitLoading={exitLoading} />}
        {activeTab === 4 && <PerformancePanel performance={performance} scanResult={scanResult} t={t} />}
        {activeTab === 5 && <SignalHistoryPanel />}
        {activeTab === 6 && <PaperTradingPanel paperPositions={paperPositions} formatNumber={fmt} formatPercent={fmtPct} />}
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ fontWeight: 600 }}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TPlus;
